/**
 * SFX manifest schema + validator (spec.md §N task §7). An event-name → entry map.
 * Each entry plays either real audio files (random variant) or a synthesized WebAudio
 * tone fallback — final audio is not required in this phase, so tone-only entries let
 * the whole event system run with **no binary assets** (placeholder-audio policy).
 *
 * Validation is fail-fast (like the animation schema) and collects all errors.
 */

export interface ToneSpec {
  /** Oscillator type; defaults to 'sine' at play time. */
  type?: 'sine' | 'square' | 'triangle' | 'sawtooth';
  /** Base frequency (Hz). */
  freq: number;
  durationMs: number;
  /** Optional linear frequency sweep target (Hz) for a quick "blip" feel. */
  sweepTo?: number;
}

export interface SfxEntry {
  /** Audio file paths relative to the audio base. A random one is chosen per play. */
  files?: string[];
  /** Synthesized fallback (used when there are no files, or a file fails to load). */
  tone?: ToneSpec;
  /** Per-entry gain 0..1 (folded with sfx + master volume). Default 1. */
  volume?: number;
  /** Inclusive [min,max] playback-rate range for a randomized pitch. */
  randomPitch?: [number, number];
  /** Minimum ms between successive plays of this key (de-spam). Default 0. */
  cooldownMs?: number;
}

export type AudioManifest = Record<string, SfxEntry>;

export type AudioManifestResult =
  | { valid: true; manifest: AudioManifest }
  | { valid: false; errors: string[] };

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function validateTone(where: string, tone: unknown, errors: string[]): void {
  if (!isObject(tone)) {
    errors.push(`${where}.tone must be an object`);
    return;
  }
  if (!(typeof tone.freq === 'number' && tone.freq > 0)) errors.push(`${where}.tone.freq must be a positive number`);
  if (!(typeof tone.durationMs === 'number' && tone.durationMs > 0)) errors.push(`${where}.tone.durationMs must be a positive number`);
  if (tone.sweepTo !== undefined && !(typeof tone.sweepTo === 'number' && tone.sweepTo > 0)) {
    errors.push(`${where}.tone.sweepTo must be a positive number when present`);
  }
  const types = ['sine', 'square', 'triangle', 'sawtooth'];
  if (tone.type !== undefined && !types.includes(tone.type as string)) {
    errors.push(`${where}.tone.type must be one of ${types.join(', ')}`);
  }
}

function validateEntry(key: string, entry: unknown, errors: string[]): void {
  const where = `"${key}"`;
  if (!isObject(entry)) {
    errors.push(`${where} must be an object`);
    return;
  }
  const hasFiles = entry.files !== undefined;
  const hasTone = entry.tone !== undefined;
  if (!hasFiles && !hasTone) {
    errors.push(`${where} must define "files" or a "tone" (nothing to play)`);
  }
  if (hasFiles) {
    if (!Array.isArray(entry.files) || entry.files.length === 0) {
      errors.push(`${where}.files must be a non-empty array when present`);
    } else if (!entry.files.every((f) => typeof f === 'string' && f.length > 0)) {
      errors.push(`${where}.files must contain only non-empty strings`);
    }
  }
  if (hasTone) validateTone(where, entry.tone, errors);
  if (entry.volume !== undefined && !(typeof entry.volume === 'number' && entry.volume >= 0)) {
    errors.push(`${where}.volume must be a number >= 0 when present`);
  }
  if (entry.randomPitch !== undefined) {
    const rp = entry.randomPitch;
    if (!Array.isArray(rp) || rp.length !== 2 || typeof rp[0] !== 'number' || typeof rp[1] !== 'number' || rp[0] <= 0 || rp[0] > rp[1]) {
      errors.push(`${where}.randomPitch must be [min,max] with 0 < min <= max`);
    }
  }
  if (entry.cooldownMs !== undefined && !(typeof entry.cooldownMs === 'number' && entry.cooldownMs >= 0)) {
    errors.push(`${where}.cooldownMs must be a number >= 0 when present`);
  }
}

export function validateAudioManifest(input: unknown): AudioManifestResult {
  if (!isObject(input)) return { valid: false, errors: ['audio manifest must be an object'] };
  const errors: string[] = [];
  for (const key of Object.keys(input)) validateEntry(key, input[key], errors);
  if (errors.length > 0) return { valid: false, errors };
  return { valid: true, manifest: input as unknown as AudioManifest };
}

export function assertValidAudioManifest(input: unknown, source = 'audio manifest'): AudioManifest {
  const result = validateAudioManifest(input);
  if (!result.valid) {
    throw new Error(`Invalid ${source}:\n  - ${result.errors.join('\n  - ')}`);
  }
  return result.manifest;
}
